import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from 'app/core/user/user.model';
import { UserService } from 'app/core/user/user.service';
import { AuthService } from 'app/core/auth/auth.service';
import { webpages_Role } from 'app/shared/models/domain/data.model';
import { environment } from 'environments/environment';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { CamelCaseToWordsPipe } from 'app/shared/pipes/camel-case-to-words.pipe';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';

import { AlertService } from 'app/shared/services/alert.service';
import { StorageCleanupService } from 'app/core/service';
import { formatBuildInfo } from 'app/shared/functions/build-info.functions';

@Component({
    selector: 'user-menu',
    templateUrl: './user-menu.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'userMenu',
    standalone: true,
    imports: [MatIconButton, MatMenuTrigger, MatIcon, MatMenu, MatMenuItem, MatDivider, RouterLink]
})
export class UserMenuComponent implements OnInit, OnDestroy {
     
    static ngAcceptInputType_showAvatar: BooleanInput;
     

    @Input() showAvatar: boolean = true;
    user: User;
    roleString: string = '(unknown)';
    roles: any;
    selectedTab: string;
    tabs: string[];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _router: Router,
        private readonly _userService: UserService,
        private readonly dashboardService: DashboardService,
        private readonly authService: AuthService,
        private readonly camelPipe: CamelCaseToWordsPipe,
        private readonly alertService: AlertService,
        private readonly storageCleanupService: StorageCleanupService
    ) {
    }

    ngOnInit(): void {
        if (this.authService.localUserRoles?.length) {
            this.tabs = [];
            this.roles = this.authService.localUserRoles;
            this.roleString = this.authService.localUserRoles.map(r => r.roleName).join(', ');
            this.authService.localUserRoles
                .forEach((role: webpages_Role) => {

                    Object.keys(environment.roleTabs)
                        .forEach((key) => {
                            if (environment.roleTabs[key].indexOf(role.roleName) > -1
                                && this.tabs.indexOf(this.camelPipe.transform(key)) == -1) {
                                this.tabs.push(this.camelPipe.transform(key));
                            }
                        });
                });

            if (isNaN(this.dashboardService.tabIndex)) {
                this.onChangeRole(0, false);
            } else {
                this.onChangeRole(this.dashboardService.tabIndex, false);
            }
        }
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    onChangeRole(idx: number, reload = false): any {
        this.dashboardService.tabIndex = idx;
        this.selectedTab = this.tabs[this.dashboardService.tabIndex];
        if (reload) {
            this._router.navigate(['/reload'], { replaceUrl: true }).then(() => {
                this._router.navigate(['/home'], { replaceUrl: true });
            });
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    updateUserStatus(status: string): void {
        if (!this.user) {
            return;
        }

        this._userService.update({
            ...this.user,
            status
        }).subscribe();
    }

    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    profile(): void {
        this._router.navigate(['/profile']);
    }

    changePassword(): void {
        this._router.navigate(['/change-password']);
    }

    users(): void {
        this._router.navigate(['/users']);
    }

    goToBuild(): void {
        this.alertService.success(formatBuildInfo(environment.build), 'Build');
    }

    clearCache(): void {
        this.alertService.confirm('Are you sure you want to clear the cache? This will remove saved filters and other cached data stored in the browser.', ' ').then((result) => {
            if (result?.isConfirmed) {
                this.performCacheCleanup();
            }
        });
    }

    private performCacheCleanup(): void {
        this.storageCleanupService.cleanupAll()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (result.indexedDbCleared && result.localStorageCleared) {
                    this._router.navigate(['/reload'], { replaceUrl: true }).then(() => {
                        this._router.navigate(['/home'], { replaceUrl: true });
                    });
                    this.alertService.successToast('Clear Cache', 'Cache cleared successfully.');
                } else {
                    this.alertService.warnToast('Clear Cache', 'Cache cleanup did not fully succeed. Check console for details.');
                }
            });
    }
}
